/**
 * Custom hooks for Shipments API
 *
 * Provides hooks for creating, updating, and deleting shipments/containers
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPut, apiDelete } from "@/lib/api-client";
import { CONTAINERS_RECAP_QUERY_KEYS, type ShipmentResponse } from "./use-containers-recap";

// ============================================================================
// TYPES
// ============================================================================

export interface ShipmentCreateData {
  company_id: string;

  // Booking (new primary identifier - 2025 model)
  booking_number?: string;  // Carrier booking reference (required in new workflow)
  bl_number?: string;       // Bill of Lading number (optional, needed for tracking)
  container_number?: string; // ISO container number (optional, assigned by carrier later)

  // Parties
  shipper_company_id?: string;
  consignee_company_id?: string;
  freight_forwarder_id?: string;
  notify_party_company_id?: string;

  // Additional References
  service_contract_number?: string;
  purchase_order_number?: string;

  // Route
  origin_port_id?: string;
  destination_port_id?: string;
  origin_country_code?: string;
  destination_country_code?: string;

  // Transport
  cargo_type?: string;
  transport_mode?: string;
  container_type?: string;
  container_size?: string;
  incoterms?: string;

  // Vessel/Flight
  vessel_name?: string;
  voyage_number?: string;
  carrier_name?: string;

  // Financial
  total_value_usd?: number;
  currency?: string;

  // Weight & Volume
  total_weight_kg?: number;
  total_volume_cbm?: number;
  total_packages?: number;

  // Cargo
  cargo_description?: string;
  hs_code_primary?: string;

  // Status
  status?: string;
  physical_status?: string;

  // Timeline
  booking_date?: string;
  estimated_departure_date?: string;
  actual_departure_date?: string;
  estimated_arrival_date?: string;
  actual_arrival_date?: string;

  // Special Requirements
  requires_temperature_control?: boolean;
  temperature_setpoint_celsius?: number;
  is_hazardous?: boolean;
  hazard_class?: string;

  // Notes & Metadata
  internal_notes?: string;
  customer_notes?: string;
  special_instructions?: string;
  extra_metadata?: Record<string, unknown>;
}

export interface ShipmentUpdateData extends Omit<ShipmentCreateData, "company_id"> {}

export interface DeleteResponse {
  message: string;
  shipment_id: string;
  container_number: string;
}

export interface ArchiveResponse {
  message: string;
  shipment_id: string;
  archived_at: string;
}

export interface SyncTrackingResponse {
  success: boolean;
  skipped?: boolean;
  error?: string;
  container_id?: string;
  container_number?: string;
  events_created?: number;
  status_updated?: boolean;
  fields_updated?: string[];
  last_tracked_at?: string;
  details?: string;
}

// ============================================================================
// CREATE SHIPMENT HOOK
// ============================================================================

/**
 * Hook to create a new shipment/container
 *
 * On success, invalidates the containers recap queries to refresh the list
 */
export function useCreateShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ShipmentCreateData): Promise<ShipmentResponse> => {
      return await apiPost<ShipmentResponse>("/shipments", data);
    },
    onSuccess: () => {
      // Invalidate all containers recap queries to refresh lists
      queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
    },
  });
}

// ============================================================================
// UPDATE SHIPMENT HOOK
// ============================================================================

/**
 * Hook to update an existing shipment/container
 *
 * @param shipmentId - UUID of the shipment to update
 *
 * On success, invalidates both the specific shipment query and recap queries
 */
export function useUpdateShipment(shipmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ShipmentUpdateData): Promise<ShipmentResponse> => {
      return await apiPut<ShipmentResponse>(`/shipments/${shipmentId}`, data);
    },
    onSuccess: () => {
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: ["shipment", shipmentId] });
      // Invalidate all containers recap queries
      queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
    },
  });
}

// ============================================================================
// DELETE SHIPMENT HOOK (PERMANENT - CASCADE)
// ============================================================================

/**
 * Hook to permanently delete a shipment/container and ALL related data.
 *
 * **WARNING: This action is IRREVERSIBLE.**
 *
 * Deletes: documents, events, issues, notes, stakeholders, orders, emails, activities.
 *
 * **Requires admin role** - will return 403 if user is not admin
 *
 * For soft delete (archive), use useArchiveShipment instead.
 *
 * On success, invalidates all containers recap queries
 */
export function useDeleteShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shipmentId: string): Promise<DeleteResponse> => {
      return await apiDelete<DeleteResponse>(`/shipments/${shipmentId}`);
    },
    onSuccess: (_, shipmentId) => {
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: ["shipment", shipmentId] });
      // Invalidate all containers recap queries
      queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
    },
  });
}

// ============================================================================
// ARCHIVE SHIPMENT HOOK (SOFT DELETE)
// ============================================================================

/**
 * Hook to archive a shipment/container (soft delete).
 *
 * Archived containers are hidden from the list but all data is preserved
 * and can be restored later.
 *
 * **Requires admin role** - will return 403 if user is not admin
 *
 * For permanent deletion, use useDeleteShipment instead.
 *
 * On success, invalidates all containers recap queries
 */
export function useArchiveShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shipmentId: string): Promise<ArchiveResponse> => {
      return await apiPost<ArchiveResponse>(`/shipments/${shipmentId}/archive`);
    },
    onSuccess: (_, shipmentId) => {
      // Invalidate the specific shipment query
      queryClient.invalidateQueries({ queryKey: ["shipment", shipmentId] });
      // Invalidate all containers recap queries
      queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
    },
  });
}

// ============================================================================
// SYNC CONTAINER TRACKING HOOK
// ============================================================================

/**
 * Hook to manually trigger a tracking refresh for a container via JSONCargo.
 *
 * Calls POST /tracking/containers/{containerId}/sync (backend uses force=True by default
 * so JSONCargo is always attempted; field updates respect source priority).
 * On success, invalidates shipment and containers-recap queries so the detail page
 * and list both show updated last_tracked_at and tracking data.
 */
export function useSyncContainerTracking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (containerId: string): Promise<SyncTrackingResponse> => {
      return await apiPost<SyncTrackingResponse>(`/tracking/containers/${containerId}/sync?force=true`);
    },
    onSuccess: (_, containerId) => {
      queryClient.invalidateQueries({ queryKey: ["shipment", containerId] });
      queryClient.invalidateQueries({ queryKey: CONTAINERS_RECAP_QUERY_KEYS.all });
    },
  });
}
